import type { WidgetProps } from "../registry";
import type { ProgramPayload } from "../../../generated-api";
import { useVideoSelector } from "../../../hooks/useVideoSearch";
import { useState, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  submitProgramPipeline,
  VideoResultType,
  VideoSelectionMap,
} from "../actions/createProgram";

function ExerciseVideoBlock({
  query,
  index,
  onVideoSelect,
}: {
  query: string;
  index: number;
  onVideoSelect: (index: number, video: VideoResultType) => void;
}) {
  const { results, selectedVideo, selectedIndex, setSelectedIndex, isLoading } =
    useVideoSelector(query);

  // Track state changes by syncing with unique URL strings
  const lastSyncRef = useRef<string | null>(null);

  if (selectedVideo && lastSyncRef.current !== selectedVideo.url) {
    lastSyncRef.current = selectedVideo.url;
    setTimeout(() => {
      // Cast safely into your matching exact type layout
      onVideoSelect(index, selectedVideo as VideoResultType);
    }, 0);
  }

  return (
    <div className="mt-2">
      <div className="aspect-video w-full rounded-lg overflow-hidden border border-border-subtle bg-black">
        {selectedVideo?.embedUrl ? (
          <iframe
            src={selectedVideo.embedUrl}
            className="w-full h-full"
            allowFullScreen
          />
        ) : (
          <div className="text-xs text-text-tertiary flex items-center justify-center h-full">
            {isLoading ? "Loading videos..." : "No video available"}
          </div>
        )}
      </div>

      {results.length > 1 && (
        <div className="flex gap-2 flex-wrap mt-2">
          {results.map((v, i) => (
            <button
              key={v.url}
              type="button"
              onClick={() => setSelectedIndex(i)}
              className={`text-xs px-2 py-1 rounded border ${
                i === selectedIndex
                  ? "bg-indigo-600 text-white"
                  : "border-border"
              }`}
            >
              {v.title?.slice(0, 25) ?? `Video ${i + 1}`}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function ProgramPreviewWidget({
  payload,
  onComplete,
  journeyPhase,
}: WidgetProps<ProgramPayload>) {
  const queryClient = useQueryClient();
  const [videoSelections, setVideoSelections] = useState<VideoSelectionMap>({});

  const shouldDetachTargets =
    journeyPhase?.kind === "generate_program" &&
    !journeyPhase.current_targets?.length;
  const effectivePayload = shouldDetachTargets
    ? { ...payload, targetIds: [] }
    : payload;
  const createProgramMutation = useMutation({
    mutationFn: () =>
      submitProgramPipeline({ payload: effectivePayload, videoSelections }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["programs"] });
      queryClient.invalidateQueries({ queryKey: ["videos"] });

      onComplete({
        accepted: true,
        message: `Created "${payload.title}"`,
      });
    },
  });

  const handleVideoSelect = (index: number, video: VideoResultType) => {
    setVideoSelections((prev) => ({
      ...prev,
      [index]: { videoData: video },
    }));
  };

  return (
    <div className="flex flex-col gap-3 mt-1 border border-border-subtle rounded-xl p-3 text-sm">
      {/* Header */}
      <div>
        <div className="font-semibold text-text-primary text-base">
          {payload.title}
        </div>
        {payload.description && (
          <div className="text-text-secondary text-xs mt-1">
            {payload.description}
          </div>
        )}
      </div>

      {/* Frequency */}
      {payload.notificationDays != null && (
        <div className="text-xs text-text-secondary">
          <span className="font-medium text-text-primary">Frequency:</span>{" "}
          {payload.notificationDays.join(", ")}
        </div>
      )}

      {/* Exercises */}
      {payload?.exercises?.length > 0 && (
        <div>
          <div className="text-xs font-medium text-text-primary mb-1">
            Program exercises ({payload.exercises.length})
          </div>

          <ul className="flex flex-col gap-2 max-h-96 overflow-auto pr-1">
            {payload.exercises.map((exercise, i) => (
              <li
                key={`${exercise.title}-${i}`}
                className="text-xs text-text-secondary border border-border-subtle rounded-md p-2"
              >
                <div className="text-text-primary font-medium">
                  {i + 1}. {exercise.title}
                </div>
                {exercise.description && (
                  <div className="mt-0.5">{exercise.description}</div>
                )}

                <ExerciseVideoBlock
                  query={exercise.query || exercise.title}
                  index={i}
                  onVideoSelect={handleVideoSelect}
                />
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Errors */}
      {createProgramMutation.isError && (
        <div className="text-xs text-red-600 bg-red-50 p-2 rounded-md">
          {(createProgramMutation.error as any)?.message ||
            "Failed to create program"}
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2 pt-1">
        <button
          type="button"
          disabled={createProgramMutation.isPending}
          onClick={() => createProgramMutation.mutate()}
          className="px-4 py-2 text-sm font-medium rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 transition-colors"
        >
          {createProgramMutation.isPending
            ? "Creating..."
            : "Create this program"}
        </button>

        <button
          type="button"
          disabled={createProgramMutation.isPending}
          onClick={() => onComplete({ accepted: false })}
          className="px-4 py-2 text-sm font-medium rounded-lg border border-border-subtle text-text-primary hover:bg-bg-secondary transition-colors"
        >
          Let's adjust it
        </button>
      </div>
    </div>
  );
}
